import { ChangeEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import imgWhiteLogo1 from "../assets/2ccda97ecaa4b401fe79d7f0575e654788a45c27.png";

const OTHER_OPTION = "Other - Specify";

type SurveyResponses = {
  firstName: string;
  lastName: string;
  postalCode: string;
  email: string;
  direction: string;
  principles: string;
  priorities: [string, string, string];
  otherPriority: string;
  role: string;
};

const initialResponses: SurveyResponses = {
  firstName: "",
  lastName: "",
  postalCode: "",
  email: "",
  direction: "",
  principles: "",
  priorities: ["", "", ""],
  otherPriority: "",
  role: "",
};

const directionOptions = [
  "Very positive",
  "Somewhat positive",
  "Somewhat negative",
  "Very negative",
];

const principlesOptions = [
  "Yes, they are",
  "Somewhat, but they've lost focus",
  "No, they've abandoned those principles",
  "Unsure",
];

const priorityOptions = [
  "Economy - Make the Ontario economy more attractive to investment and business",
  "Healthcare - Reform healthcare to improve quality and reduce wait times",
  "Education - Put parents in control of their kids' educations",
  "Childcare - Improve access to childcare for all",
  "Housing - Make housing more affordable and easier to build",
  "Public Safety - Protect our communities and make our streets safe",
  "Infrastructure - Strengthen our economy and reduce congestion by investing in infrastructure",
  "Accountability - Restore accountability and integrity at Queens Park",
  OTHER_OPTION,
];

const roleOptions = [
  "Develop serious conservative policy ideas to influence the public discourse and the current trajectory of the PC Government",
  "Build a grassroots network to replace Doug Ford as leader of the PC Party",
  "Support Doug Ford and his government at all costs to avoid the risk of losing an election",
];

const SURVEY_FORM_ACTION =
  "https://docs.google.com/forms/d/e/1FAIpQLSdwzIVH3PrHsxMqpbkksyPz5QeeSnb5OUrsFcdrx_P8uzz_uQ/formResponse";
const ENTRY_DIRECTION = "entry.1132339892";
const ENTRY_PRIORITIES = "entry.531751822";
const ENTRY_ROLE = "entry.1320326638";
const ENTRY_FIRST_NAME = "entry.539743631";
const ENTRY_LAST_NAME = "entry.1850172997";
const ENTRY_EMAIL = "entry.1088924247";
const ENTRY_POSTAL_CODE = "entry.1846931735";
const ENTRY_PRINCIPLES = "entry.711440318";

type SocialShareButtonProps = {
  label: string;
  href: string;
  bgClass: string;
  textClass?: string;
  isExternal?: boolean;
};

function SocialShareButton({ label, href, bgClass, textClass, isExternal = true }: SocialShareButtonProps) {
  const baseClasses =
    "px-4 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-transform duration-150 hover:scale-105";
  const combinedClasses = `${baseClasses} ${bgClass} ${textClass ?? "text-white"}`;
  const target = isExternal ? "_blank" : undefined;
  const rel = isExternal ? "noopener noreferrer" : undefined;

  return (
    <a href={href} className={combinedClasses} target={target} rel={rel}>
      {label}
    </a>
  );
}

export default function SurveyPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<SurveyResponses>(initialResponses);
  const [stepError, setStepError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const totalSteps = 5;

  const progressPercentage = useMemo(() => {
    if (totalSteps === 0) return 0;
    return Math.round(((currentStep + 1) / totalSteps) * 100);
  }, [currentStep, totalSteps]);

  const handleSingleSelect = (key: "direction" | "principles" | "role", value: string) => {
    setResponses((prev) => ({
      ...prev,
      [key]: value,
    }));
    setStepError("");
  };

  const handleContactChange = (key: "firstName" | "lastName" | "postalCode" | "email", value: string) => {
    setResponses((prev) => ({
      ...prev,
      [key]: value,
    }));
    setStepError("");
  };

  const handlePriorityChange = (index: number, event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setResponses((prev) => {
      const updated = [...prev.priorities] as SurveyResponses["priorities"];

      updated.forEach((option, optionIndex) => {
        if (optionIndex !== index && option === value) {
          updated[optionIndex] = "";
        }
      });

      updated[index] = value;
      const includesOther = updated.includes(OTHER_OPTION);

      return {
        ...prev,
        priorities: updated,
        otherPriority: includesOther ? prev.otherPriority : "",
      };
    });
    setStepError("");
  };

  const handleOtherPriorityChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setResponses((prev) => ({
      ...prev,
      otherPriority: value,
    }));
    setStepError("");
  };

  const formatPrioritiesForSubmission = () => {
    return responses.priorities
      .map((priority) => {
        if (priority === OTHER_OPTION) {
          return `Other: ${responses.otherPriority.trim()}`;
        }
        return priority;
      })
      .join("\n");
  };

  const submitSurveyResponses = async () => {
    const payload = new URLSearchParams();
    payload.append(ENTRY_FIRST_NAME, responses.firstName.trim());
    payload.append(ENTRY_LAST_NAME, responses.lastName.trim());
    payload.append(ENTRY_POSTAL_CODE, responses.postalCode.trim());
    payload.append(ENTRY_EMAIL, responses.email.trim());
    payload.append(ENTRY_DIRECTION, responses.direction);
    payload.append(ENTRY_PRINCIPLES, responses.principles);
    payload.append(ENTRY_PRIORITIES, formatPrioritiesForSubmission());
    payload.append(ENTRY_ROLE, responses.role);

    await fetch(SURVEY_FORM_ACTION, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: payload.toString(),
    });
  };

  const validateStep = () => {
    if (currentStep === 0) {
      if (!responses.firstName.trim() || !responses.lastName.trim() || !responses.postalCode.trim() || !responses.email.trim()) {
        setStepError("Please complete all fields before continuing.");
        return false;
      }
      const emailValid = /\S+@\S+\.\S+/.test(responses.email);
      if (!emailValid) {
        setStepError("Please enter a valid email address.");
        return false;
      }
    }

    if (currentStep === 1 && !responses.direction) {
      setStepError("Please select the option that best matches how you feel.");
      return false;
    }

    if (currentStep === 2 && !responses.principles) {
      setStepError("Please choose the response that best reflects your view.");
      return false;
    }

    if (currentStep === 3) {
      const hasEmpty = responses.priorities.some((priority) => !priority);
      if (hasEmpty) {
        setStepError("Please rank your top three priorities.");
        return false;
      }
      if (responses.priorities.includes(OTHER_OPTION) && !responses.otherPriority.trim()) {
        setStepError("Please specify what 'Other' means to you.");
        return false;
      }
    }

    if (currentStep === 4 && !responses.role) {
      setStepError("Please select the option that best reflects your view.");
      return false;
    }

    setStepError("");
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setStepError("");
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      return;
    }
    setSubmissionError("");
    setIsSubmittingForm(true);
    try {
      await submitSurveyResponses();
      setIsSubmitted(true);
    } catch (error) {
      console.error("Survey submission failed", error);
      setSubmissionError("Submission failed. Please try again.");
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#00499d] flex flex-col">
      <header className="bg-[#00499d] w-full border-b border-white/20 py-4">
        <div className="max-w-[1200px] mx-auto px-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white flex items-center gap-2 hover:opacity-80">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div className="flex items-center gap-3">
            <img src={imgWhiteLogo1} alt="Project Ontario" className="h-8 w-8" />
            <div className="text-white font-black tracking-tight leading-5">
              <div>PROJECT</div>
              <div>ONTARIO</div>
            </div>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-[800px] w-full">
          <div className="bg-white/10 border border-white/20 rounded-2xl p-8 md:p-12 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Ontario Conservative Pulse Check</h1>
                  <p className="text-white/70 max-w-[520px]">
                    Thank you for sharing your perspective. Your responses help guide Project Ontario's strategy and priorities.
                  </p>
                </div>
                <div className="text-white/80 text-right">
                  <div className="font-bold text-lg">
                    Step {currentStep + 1} of {totalSteps}
                  </div>
                  <div className="text-sm">Progress {progressPercentage}%</div>
                </div>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-8">
                <div
                  className="h-full bg-white transition-all duration-300 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>

              {!isSubmitted ? (
                <>
                  {currentStep === 0 && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-white mb-4">1. Tell us a bit about yourself</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex flex-col text-white/80 text-sm gap-2">
                          First Name
                          <input
                            type="text"
                            name="firstName"
                            autoComplete="given-name"
                            className="rounded-2xl px-4 py-3 text-[#00499d] font-semibold focus:outline-none focus:ring-2 focus:ring-white/80"
                            value={responses.firstName}
                            onChange={(event) => handleContactChange("firstName", event.target.value)}
                            placeholder="Jane"
                            style={{ WebkitTextFillColor: "#00499d" }}
                          />
                        </label>
                        <label className="flex flex-col text-white/80 text-sm gap-2">
                          Last Name
                          <input
                            type="text"
                            name="lastName"
                            autoComplete="family-name"
                            className="rounded-2xl px-4 py-3 text-[#00499d] font-semibold focus:outline-none focus:ring-2 focus:ring-white/80"
                            value={responses.lastName}
                            onChange={(event) => handleContactChange("lastName", event.target.value)}
                            placeholder="Doe"
                            style={{ WebkitTextFillColor: "#00499d" }}
                          />
                        </label>
                        <label className="flex flex-col text-white/80 text-sm gap-2">
                          Postal Code
                          <input
                            type="text"
                            name="postalCode"
                            autoComplete="postal-code"
                            className="rounded-2xl px-4 py-3 text-[#00499d] font-semibold focus:outline-none focus:ring-2 focus:ring-white/80"
                            value={responses.postalCode}
                            onChange={(event) => handleContactChange("postalCode", event.target.value.toUpperCase())}
                            placeholder="A1A 1A1"
                            style={{ WebkitTextFillColor: "#00499d" }}
                          />
                        </label>
                        <label className="flex flex-col text-white/80 text-sm gap-2">
                          Email
                          <input
                            type="email"
                            name="email"
                            autoComplete="email"
                            className="rounded-2xl px-4 py-3 text-[#00499d] font-semibold focus:outline-none focus:ring-2 focus:ring-white/80"
                            value={responses.email}
                            onChange={(event) => handleContactChange("email", event.target.value)}
                            placeholder="you@example.com"
                            style={{ WebkitTextFillColor: "#00499d" }}
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {currentStep === 1 && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-white mb-4">
                        2. How do you feel about the direction Ontario is heading today?
                      </h2>
                      <div className="grid gap-3">
                        {directionOptions.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleSingleSelect("direction", option)}
                            type="button"
                            className={`text-left px-5 py-4 rounded-2xl border transition-all duration-200 ${
                              responses.direction === option
                                ? "border-white bg-white/20 text-white font-semibold"
                                : "border-white/30 text-white/80 hover:border-white/60 hover:bg-white/10"
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-white mb-4">
                        3. Do you believe Premier Doug Ford and the Ontario PC Party are living up to conservative principles?
                      </h2>
                      <div className="grid gap-3">
                        {principlesOptions.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleSingleSelect("principles", option)}
                            type="button"
                            className={`text-left px-5 py-4 rounded-2xl border transition-all duration-200 ${
                              responses.principles === option
                                ? "border-white bg-white/20 text-white font-semibold"
                                : "border-white/30 text-white/80 hover:border-white/60 hover:bg-white/10"
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-white mb-4">
                        4. Which of these issues should be Ontario's top priorities? (Rank your top 3)
                      </h2>
                      <div className="space-y-4">
                        {[0, 1, 2].map((slot) => (
                          <div key={slot} className="flex flex-col gap-2">
                            <label className="text-white/80 text-sm font-semibold">Priority {slot + 1}</label>
                            <select
                              value={responses.priorities[slot]}
                              onChange={(event) => handlePriorityChange(slot, event)}
                              className="rounded-full px-4 py-3 text-[#00499d] font-semibold focus:outline-none focus:ring-2 focus:ring-white/80"
                            >
                              <option value="">Select an issue</option>
                              {priorityOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                        {responses.priorities.includes(OTHER_OPTION) && (
                          <div className="flex flex-col gap-2">
                            <label className="text-white/80 text-sm font-semibold">If other, please specify</label>
                            <input
                              type="text"
                              className="rounded-full px-4 py-3 text-[#00499d] font-semibold focus:outline-none focus:ring-2 focus:ring-white/80"
                              value={responses.otherPriority}
                              onChange={handleOtherPriorityChange}
                              placeholder="Tell us what matters most to you"
                            />
                          </div>
                        )}
                        <p className="text-white/60 text-sm">
                          Select three distinct priorities. Choosing a new issue will replace duplicates automatically.
                        </p>
                      </div>
                    </div>
                  )}

                  {currentStep === 4 && (
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-white mb-4">
                        5. What role do you think Project Ontario should play?
                      </h2>
                      <div className="grid gap-3">
                        {roleOptions.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleSingleSelect("role", option)}
                            type="button"
                            className={`text-left px-5 py-4 rounded-2xl border transition-all duration-200 ${
                              responses.role === option
                                ? "border-white bg-white/20 text-white font-semibold"
                                : "border-white/30 text-white/80 hover:border-white/60 hover:bg-white/10"
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {stepError && <p className="text-sm text-red-200 mb-6">{stepError}</p>}

                  <div className="flex flex-col md:flex-row md:justify-between gap-4">
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={currentStep === 0 || isSubmittingForm}
                        className={`px-6 py-3 rounded-full font-semibold transition-all duration-150 ${
                          currentStep === 0 || isSubmittingForm
                            ? "bg-white/20 text-white/40 cursor-not-allowed"
                            : "bg-white/10 border border-white/40 text-white hover:bg-white/20"
                        }`}
                      >
                        Back
                      </button>
                    </div>
                    <div className="flex gap-3 justify-end">
                      {currentStep < totalSteps - 1 && (
                        <button
                          type="button"
                          onClick={handleNext}
                          disabled={isSubmittingForm}
                          className={`px-6 py-3 rounded-full font-bold bg-white text-[#00499d] transition-transform duration-150 ${
                            isSubmittingForm ? "opacity-60 cursor-not-allowed" : "hover:scale-105"
                          }`}
                        >
                          {isSubmittingForm ? "Please wait..." : "Next"}
                        </button>
                      )}
                      {currentStep === totalSteps - 1 && (
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={isSubmittingForm}
                          className={`px-6 py-3 rounded-full font-bold bg-white text-[#00499d] transition-transform duration-150 ${
                            isSubmittingForm ? "opacity-60 cursor-not-allowed" : "hover:scale-105"
                          }`}
                        >
                          {isSubmittingForm ? "Submitting..." : "Submit Survey"}
                        </button>
                      )}
                    </div>
                  </div>
                  {submissionError && (
                    <p className="text-sm text-red-200 text-center mt-4">{submissionError}</p>
                  )}
                </>
              ) : (
                <div className="text-center text-white space-y-6">
                  <h2 className="text-3xl font-bold">Thank you for completing the survey!</h2>
                  <p className="text-white/80 max-w-[540px] mx-auto">
                    Your feedback helps Project Ontario focus on the work that matters most to our supporters. We will use these insights to sharpen our priorities and report back soon.
                  </p>
                  <div className="space-y-4">
                    <p className="text-white/80 text-sm uppercase tracking-wide">Share Project Ontario</p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <SocialShareButton
                        label="Share on Facebook"
                        bgClass="bg-[#1877F2]"
                        href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fprojectontario.ca"
                      />
                      <SocialShareButton
                        label="Share on LinkedIn"
                        bgClass="bg-[#0A66C2]"
                        href="https://www.linkedin.com/shareArticle?mini=true&url=https%3A%2F%2Fprojectontario.ca"
                      />
                      <SocialShareButton
                        label="Share on X (Twitter)"
                        bgClass="bg-black"
                        href="https://twitter.com/intent/tweet?url=https%3A%2F%2Fprojectontario.ca&text=Project%20Ontario%20%7C%20Conservative%20Renewal"
                      />
                      <SocialShareButton
                        label="Share on Instagram"
                        bgClass="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400"
                        href="https://www.instagram.com/"
                        isExternal
                      />
                      <SocialShareButton
                        label="Share via Email"
                        bgClass="bg-white/10 border border-white/40 hover:bg-white/20"
                        textClass="text-[#00499d]"
                        href="mailto:?subject=Project%20Ontario%20Survey&body=I%20just%20completed%20the%20Project%20Ontario%20supporter%20survey.%20Check%20out%20their%20work%20at%20https%3A%2F%2Fprojectontario.ca"
                        isExternal={false}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
}
